/**
 * @file pages/LoginPage.jsx
 * @description Branded login page shown to unauthenticated users.
 *
 * Presents a single "Sign in with Microsoft" button that triggers the
 * Azure Entra ID MSAL redirect flow. After a successful login, Azure
 * redirects back to the app root where main.jsx processes the auth code
 * and the user lands on the Dashboard.
 *
 * If the user is already authenticated (e.g. they navigate to /login
 * manually), they are redirected to the Dashboard immediately.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../auth/msalConfig';
import { getSetupStatus } from '../api/setup';

/**
 * Full-viewport login page with brand styling.
 * Renders a centred card with the application name, a short tagline,
 * and a "Sign in with Microsoft" call-to-action button.
 */
export default function LoginPage() {
  const isAuthenticated          = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const navigate                 = useNavigate();

  // Only show the setup wizard link when we can positively confirm setup isn't
  // done yet — hide it by default while loading/on error so it doesn't appear
  // on an already-configured instance just because the status check failed.
  const { data: setupStatus } = useQuery({
    queryKey: ['setupStatus'],
    queryFn:  getSetupStatus,
    retry:    false,
  });
  const showSetupLink = setupStatus?.setupComplete === false;

  // Redirect already-authenticated users straight to the dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  /**
   * Initiates the Azure Entra ID redirect login flow.
   * After successful authentication, Azure redirects back to the
   * configured redirectUri (app root), where main.jsx finalises the session.
   */
  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch(console.error);
  };

  const isPending = inProgress !== InteractionStatus.None;

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg)' }}
    >
      {/* ── Card ──────────────────────────────────────────────────────────── */}
      <div
        className="card border-0 shadow-lg overflow-hidden"
        style={{ width: '100%', maxWidth: 420 }}
      >
        {/* Top band — primary brand colour */}
        <div
          className="px-4 pt-4 pb-5 text-center"
          style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
        >
          {/* App icon */}
          <div
            className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: 72,
              height: 72,
              backgroundColor: 'rgba(255,255,255,0.12)',
            }}
            aria-hidden="true"
          >
            <i className="bi bi-hdd-network-fill" style={{ fontSize: 36, color: 'var(--color-accent-1)' }} />
          </div>

          <h1 className="h4 fw-bold mb-1">NET-API Admin</h1>
          <p className="mb-0 opacity-75 small">
            API token &amp; endpoint management
          </p>
        </div>

        {/* Lower section — sign-in action */}
        <div className="card-body px-4 pt-4 pb-4 text-center">
          <p className="text-muted mb-4">
            Sign in with your Microsoft account to access the admin panel.
          </p>

          <button
            type="button"
            className="btn w-100 d-flex align-items-center justify-content-center gap-2 py-2"
            style={{
              backgroundColor: 'var(--color-accent-1)',
              color: '#000',
              fontWeight: 600,
              border: 'none',
            }}
            onClick={handleLogin}
            disabled={isPending}
            aria-label="Sign in with Microsoft"
          >
            {isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                Signing in…
              </>
            ) : (
              <>
                <i className="bi bi-microsoft fs-5" aria-hidden="true" />
                Sign in with Microsoft
              </>
            )}
          </button>
        </div>
      </div>

      {/* Footer note */}
      <p className="text-muted small mt-4">
        Access is restricted to authorised NEAS personnel.
      </p>
      {showSetupLink && (
        <p className="text-muted small">
          First time running this app? <a href="/setup">Run the setup wizard</a>.
        </p>
      )}
    </div>
  );
}
